// What a call to this number is routed TO — and, on a new line, the question that has to be
// answered before the rest of the tab means anything.
//
// THREE STATES, because "not chosen" is not the same as "direct" (Kevin, 2026-08-11):
//
//   * the package includes one extension  -> there is no choice; say so and move on
//   * several allowed, nothing chosen yet -> ASK, and show nothing else on the tab until
//                                            it is answered
//   * chosen                              -> a one-line summary with a gear
//
// `routeChosen` comes from the server because `routeType` cannot carry the difference: it
// defaults to 'extension', so an untouched line looked exactly like one deliberately routed
// straight to its extension.
//
// Picking a ring group captures its settings in the same sheet. A route type saved with no
// strategy behind it is a group that rings in whatever order the host happens to hold, which
// is the kind of half-set state this page exists to stop.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import GearButton from '../shared/GearButton.jsx';
import FormModal from '../shared/FormModal.jsx';
import { Row, AttendantRows, GreetingModal, AddMenuModal, KeysModal } from './AttendantFlow.jsx';

// Sentinel for "make a new one" in the auto-attendant list.
export const NEW_IVR = '__new_ivr';

const STRATEGIES = [
  ['ringall', 'Ring all at once'],
  ['hunt', 'One at a time, in order'],
  ['memoryhunt', 'One, then two, then three…'],
  ['firstavailable', 'First one available'],
  ['firstnotonphone', 'First one not on a call'],
  ['random', 'Random order'],
];

// `SUMMARY[type] || type` used to fall through to the raw column value, so the Bud
// Depot line's card read "Calls go to: ivr" — the enum, on screen, to an operator.
// Every type the chooser can produce needs an entry here.
// What the HOST is doing, in the same voice as SUMMARY. Only used to say the two disagree.
const LIVE_WORDS = {
  extension: 'an extension',
  'ring-group': 'a ring group',
  ivr: 'an auto attendant',
  queue: 'a queue',
  'time-condition': 'a time condition',
  announcement: 'an announcement',
  voicemail: 'voicemail',
  parked: 'a not-in-service message',
  none: 'nothing',
};

const SUMMARY = {
  extension: 'Straight to the extension',
  ring_group: 'A ring group',
  queue: 'A queue',
  ivr: 'An auto attendant',
};

/**
 * Which queue / IVR on this number's host.
 *
 * A SELECT, NOT A TEXT FIELD. These ids belong to FreePBX and are per-host, so a
 * typed one would be a route to nowhere — and with the PBX write stubbed there is
 * no downstream failure to reveal it. The list comes from the host itself.
 */
function FlowTargetPicker({ line, kind, value, onChange, newName, onNewName }) {
  const { data, isLoading } = useQuery({
    queryKey: ['line', String(line.id), 'route-options'],
    queryFn: () => api.get(`/lines/${line.id}/route-options`),
    retry: false,
  });

  const list = kind === 'queue' ? (data?.queues || []) : (data?.ivrs || []);
  const unreadable = kind === 'queue' ? data?.queues_unavailable : data?.ivrs_unavailable;
  // Matches the words in the chooser above. "Auto attendant" is what an operator
  // calls it; "IVR" and "menu" are ours and FreePBX's.
  const noun = kind === 'queue' ? 'queue' : 'auto attendant';

  if (isLoading) return <p className="text-xs text-gray-500">Reading what this PBX has…</p>;

  // AN EMPTY LIST IS NOT "NONE CONFIGURED". The agent returns [] both when the
  // FreePBX module is absent and when there are genuinely none, so say which.
  if (data?.available === false || unreadable) {
    return (
      <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        {data?.reason || `Could not read the ${noun}s on this PBX, so there is nothing to choose from.`}
      </p>
    );
  }
  // "BUILD ONE IN FREEPBX FIRST — VERSO DOES NOT CREATE ONE" WAS TRUE AND IS NOT ANY MORE.
  // Creating an auto attendant landed with agent v0.52.0, and this message kept sending operators
  // away from the one screen that can now do it. A queue still has no create path, so it keeps the
  // honest version of the sentence.
  if (!list.length && kind === 'queue') {
    return (
      <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        There are no queues to choose from, and Verso cannot create one — a number can only be
        pointed at a queue that already exists.
      </p>
    );
  }

  return (
    <div>
      <label htmlFor="rt-target" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
        Which {noun}
      </label>
      <select
        id="rt-target"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
      >
        <option value="">Choose…</option>
        {list.map((o) => (
          <option key={o.id} value={o.id}>{o.label} ({o.id})</option>
        ))}
        {/* THE WAY TO MAKE ONE, in the list. Kevin, 2026-08-27: "it only let's you select an existing
            IVR/Auto attendent?" — it did, months after the endpoint to create one existed. Same
            discovery problem as the recording picker: the option has to be where the eye already is.
            Auto attendants only; there is no create path for a queue. */}
        {kind === 'ivr' && <option value={NEW_IVR}>Create a new auto attendant…</option>}
      </select>

      {/* One field, because everything else about a new menu has a sensible default: callers who
          press nothing go to voicemail, a wrong key replays it, and keys are added afterwards. */}
      {value === NEW_IVR && (
        <div className="mt-3">
          <label htmlFor="rt-new-name" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
            Name it
          </label>
          <input
            id="rt-new-name"
            value={newName}
            maxLength={50}
            onChange={(e) => onNewName(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Callers hear a greeting, then press a key. You choose the greeting next.
          </p>
        </div>
      )}
    </div>
  );
}

export default function RouteTypeCard({ line, canEdit, onSaved }) {
  const qc = useQueryClient();
  // Which modal the destination's own rows have asked for. Owned here because a modal inside a
  // `<tbody>` is invalid HTML, and because this card is the one gear for one decision.
  const [rowModal, setRowModal] = useState(null);

  // WHAT THE PBX ACTUALLY DOES, shared by query key with LiveRouteCard so this is not a second
  // request. `line.routeType` is what Verso would BUILD; a number pointed at a menu by hand still
  // reads `extension` there, and the rows below have to describe what is really answering.
  const { data: liveRoute } = useQuery({
    queryKey: ['line', String(line.id), 'route-target'],
    queryFn: () => api.get(`/lines/${line.id}/route-target`),
    retry: false,
  });
  // THE WHOLE TREE, for the levels `route-target` cannot reach. It answers one level, which was
  // enough while every tree was one level; a key that opens another menu makes the second level
  // invisible. A separate query rather than a wider route-target, so every existing reader of that
  // shape is untouched.
  const { data: routeTree } = useQuery({
    queryKey: ['line', String(line.id), 'route-tree'],
    queryFn: () => api.get(`/lines/${line.id}/route-tree`),
    retry: false,
  });
  const [sheet, setSheet] = useState(null);   // null | 'choose' | 'ring_group'
  const [pick, setPick] = useState(line.routeType || 'extension');
  const [target, setTarget] = useState(line.routeTargetId || '');
  // The stubbed result, kept so the sheet can say plainly that the PBX was not changed.
  const [flowResult, setFlowResult] = useState(null);
  // Name for a menu being created from this sheet.
  const [newIvrName, setNewIvrName] = useState('');
  const [strategy, setStrategy] = useState(line.ringStrategy || 'ringall');
  const [time, setTime] = useState(String(line.ringTime ?? 20));

  // A 200 THAT DID NOT DO THE WHOLE JOB IS NOT A SUCCESS.
  //
  // The route type saves in Verso and the ring group is created, adopted — or refused. Since
  // 2026-08-13 adoption asks the host whether the group is really there, and a host it cannot
  // reach is a refusal on purpose: unreachable is not "no group". That refusal came back in
  // `ring_group_error` and this closed the sheet on it anyway, so the operator saw a clean
  // save and a line routed to a group that does not exist.
  const save = useMutation({
    mutationFn: (body) => api.patch(`/lines/${line.id}`, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['line', String(line.id)] });
      onSaved?.();
      if (!data?.ring_group_error) setSheet(null);
    },
  });

  // A queue/IVR route goes to its own endpoint: it names an object on the HOST, so
  // the server verifies it is really there before recording the choice. The write to
  // the PBX is stubbed for now, and the response says so — which this renders rather
  // than reporting a save that reached nothing.
  // Building a new auto attendant: create it, aim the number at it, apply. The endpoint previews
  // when `apply` is absent and refuses on any blocker, so this is the confirmed path.
  // A 200 IS NOT THE SAME AS A MENU CALLERS CAN REACH. The endpoint reads the route back off the
  // host and returns `verified: false` when the number is not pointed at what it just built. This
  // used to close the sheet on any 200 and throw that field away — so on Lyons Communications line
  // 139 (2026-08-27) the menu was created, the number was never pointed at it, and the card
  // cheerfully said "An auto attendant" while every call still found nothing. Same family as the
  // stubbed-write banner below: an unperformed step must not render as a performed one.
  const createIvr = useMutation({
    mutationFn: (body) => api.post(`/lines/${line.id}/ivr`, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['line', String(line.id)] });
      qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-target'] });
      qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-options'] });
      qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-tree'] });
      onSaved?.();
      // Stay open on an unconfirmed build, so the operator sees it rather than discovering it
      // from a customer.
      if (data?.verified === false) return;
      setSheet(null);
      setNewIvrName('');
    },
  });

  const saveFlow = useMutation({
    mutationFn: (body) => api.put(`/lines/${line.id}/route`, body),
    onSuccess: (data) => { setFlowResult(data); onSaved?.(); },
  });

  const multi = line.multiExtension !== false;
  const chosen = Boolean(line.routeChosen);
  const type = line.routeType || 'extension';

  const err = (save.isError && (
    <p className="text-sm text-red-700">{save.error.body?.error || save.error.message}</p>
  )) || (save.data?.ring_group_error && (
    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
      <p className="font-medium">The route type was saved. The ring group was not.</p>
      <p className="mt-1">{save.data.ring_group_error.message}</p>
    </div>
  ));

  // ONE sheet for the whole card: the red chooser on an unrouted line and the gear on a
  // routed one open the same form, so a route type is committed one way everywhere.
  //
  // ONE FORM, ONE SAVE. The type select used to save on change while the ring-group fields
  // had their own Save and Cancel — the same sheet committing two different ways, and a
  // direct route saved before the operator had agreed to anything (Kevin, 2026-08-11).
  const chooseSheet = (
    // The shared shell, so this dismisses on Escape and announces itself as a dialog like
    // every other form. Its own copy of the markup had neither.
    <FormModal
      title={`How calls reach ${line.number || `line ${line.id}`}`}
      onClose={() => { setSheet(null); save.reset(); setPick(line.routeType || 'extension'); }}
    >
      <div className="space-y-3">
      <div>
        <label htmlFor="rt-pick" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
          Calls go to
        </label>
        <select
          id="rt-pick"
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="extension">Extension</option>
          <option value="ring_group">Ring Group</option>
          {/* Pointed at, not built. Verso creates extensions and ring groups; a queue
              or an IVR is assembled in FreePBX and this only aims the number at one
              that already exists on its host. Hence a picker, never a name field. */}
          <option value="queue">Queue</option>
          <option value="ivr">Auto Attendant</option>
        </select>
      </div>

      {pick === 'ring_group' && (
        <RingGroupFields
          line={line}
          strategy={strategy}
          setStrategy={setStrategy}
          time={time}
          setTime={setTime}
        />
      )}

      {(pick === 'queue' || pick === 'ivr') && (
        <FlowTargetPicker
          line={line}
          kind={pick}
          value={target}
          onChange={setTarget}
          newName={newIvrName}
          onNewName={setNewIvrName}
        />
      )}

      {err}
      {createIvr.isError && (
        <p className="text-sm text-red-700">
          {createIvr.error?.body?.error || createIvr.error?.message}
        </p>
      )}
      {createIvr.data?.verified === false && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">The menu was created, but this number is not sending calls to it.</p>
          <p className="mt-1">
            Callers will not hear it yet. Try saving again — if it keeps happening, ask us.
          </p>
        </div>
      )}

      {/* NEVER RENDER A STUBBED WRITE AS A DONE ONE. The choice is in Verso; the PBX
          still sends calls where it did before, and an operator who is not told that
          will believe the number has moved. */}
      {/* FOUR OUTCOMES, AND THE HEADLINE USED TO BE ONE SENTENCE FOR ALL OF THEM.
          "Recorded in Verso — the PBX has not been changed." was printed for ANY result, so
          once the write went live a route that applied and read back clean still told the
          operator nothing had happened (2026-08-27). That is the same lie as the reverse,
          pointed the other way: it invites somebody to redo work that is already done, or to
          stop trusting the page. */}
      {flowResult && (flowResult.applied && flowResult.verified ? (
        <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-900">
          <p className="font-medium">Done — calls to this number now go there.</p>
          <p className="mt-0.5">Checked against the phone system, not just requested.</p>
        </div>
      ) : flowResult.applied ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-medium">Saved, but not confirmed.</p>
          <p className="mt-0.5">{flowResult.reason || 'Reading it back did not confirm the change. Callers may still reach the old destination.'}</p>
        </div>
      ) : flowResult.stubbed ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-medium">Recorded in Verso — the phone system has not been changed.</p>
          <p className="mt-0.5">{flowResult.reason}</p>
          {flowResult.would_write && (
            <p className="mt-1 font-mono text-[11px] text-amber-800">
              would send: {flowResult.would_write}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
          <p className="font-medium">The change was not applied.</p>
          <p className="mt-0.5">{flowResult.reason}</p>
        </div>
      ))}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={save.isPending || saveFlow.isPending || createIvr.isPending
            || ((pick === 'queue' || pick === 'ivr') && !target)
            || (target === NEW_IVR && !newIvrName.trim())}
          onClick={() => {
            // CREATING ONE IS A DIFFERENT ACT from pointing at one: it writes to the phone system
            // and then aims the number at what it made. Its own endpoint, which previews first.
            if (pick === 'ivr' && target === NEW_IVR) {
              createIvr.mutate({ name: newIvrName.trim(), keys: [], apply: true });
              return;
            }
            // A queue/IVR is not something the line PATCH can express — it names an
            // object on the host, so it goes to the endpoint that verifies it exists
            // there first. That endpoint's PBX write is stubbed (routeFlowWrites.js).
            if (pick === 'queue' || pick === 'ivr') {
              saveFlow.mutate({ route_type: pick, route_target_id: target });
              return;
            }
            save.mutate(pick === 'ring_group'
              ? { route_type: 'ring_group', ring_strategy: strategy, ring_time: Number(time) }
              : { route_type: 'extension' });
          }}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => { setSheet(null); save.reset(); setPick(line.routeType || 'extension'); }}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
      </div>
    </FormModal>
  );

  // ---- 1. One extension is all the package has. There is nothing to ask. ----
  //
  // The card is the route type and nothing else — Kevin, 2026-08-11: "don't need a card that
  // describes what a direct route is." No gear either: with one extension allowed there is
  // no other type to pick and no group to configure, so a gear would open a sheet with one
  // option in it.
  if (!multi) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-900">Calls go to</h2>
        <p className="mt-1 text-sm text-gray-900">The extension, directly</p>
      </div>
    );
  }

  // ---- 2. Several are allowed and nobody has said which. ASK. ----
  if (!chosen) {
    return (
      <>
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-5">
          <h2 className="text-sm font-semibold text-red-900">
            Calls to this number have nowhere to go yet
          </h2>
          <p className="mt-1 text-sm text-red-900">
            {line.packageName || 'This package'} allows more than one extension, so how{' '}
            {line.number || 'this number'} reaches them is a choice. Nothing else on this tab
            can be set up until it is made.
          </p>
          {err}
          {canEdit ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate({ route_type: 'extension' })}
                className="rounded-md bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
              >
                Straight to one extension
              </button>
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => { save.reset(); setPick('ring_group'); setSheet('choose'); }}
                className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100 disabled:opacity-50"
              >
                A ring group…
              </button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-red-900">Ask an administrator to set this.</p>
          )}
          <p className="mt-3 text-xs text-red-800">
            Either way the build creates the extension and installs the route — this records
            which one.
          </p>
        </div>
        {sheet === 'choose' && chooseSheet}
      </>
    );
  }

  // ---- 3. Chosen. A line of text, a gear, and the rows for whatever ANSWERS. ----
  //
  // ONE DESTINATION'S ROWS, NOT TWO. The ring-group rows keyed off Verso's `route_type` and the
  // menu's rows off the live kind, so when the two disagreed BOTH rendered: on 2026-08-27 line 60
  // showed "A ring group / How it rings / Rings for / Members" and then "Greeting / Press 1 / No key
  // pressed" underneath, describing two different things as though they were one call.
  //
  // What ANSWERS is the host's answer, so the rows follow the live kind. Verso's record still gets
  // said out loud when it differs — that is a real thing an operator needs to know and the reason
  // this card shows `type` at all — but it is one sentence, not a second set of rows.
  const liveKind = liveRoute?.kind ?? null;
  const KIND_OF = { extension: 'extension', ring_group: 'ring-group', ivr: 'ivr', queue: 'queue' };
  const drifted = !!liveKind && liveKind !== 'unknown' && KIND_OF[type] !== liveKind;
  // With no live read we fall back to our own column: better the record than nothing, and the
  // fallback is visible because `liveKind` is null rather than wrong.
  const showRingRows = type === 'ring_group' && (liveKind === 'ring-group' || liveKind === null);
  const showMenuRows = liveKind === 'ivr'
    || (type === 'ivr' && liveKind === null)
    // A line whose host has no menu gets the one row that offers to build one.
    || liveKind === 'extension' || liveKind === 'ring-group';

  return (
    <>
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Calls go to</h2>
            <p className="mt-1 text-sm text-gray-900">
              {SUMMARY[type] || type}
              {/* WHICH group. Saving the type now creates or adopts one, numbered from the
                  primary extension (0 where the extension has 1), so the card names it —
                  otherwise "a ring group" is a claim the operator cannot check. */}
              {type === 'ring_group' && line.ringGroupNum && (
                <span className="ml-2 font-mono text-xs text-gray-500">{line.ringGroupNum}</span>
              )}
              {/* Same reason: "an auto attendant" is a claim the operator cannot check without
                  being told which one. THE NAME, NOT THE ID — a bare monospace "10" is
                  FreePBX's primary key and says nothing to the person reading it. The live read
                  carries the name; when it has not arrived there is nothing worth showing, so
                  nothing is shown rather than the number. */}
              {(type === 'ivr' || type === 'queue') && liveRoute?.object?.name && (
                <span className="ml-2 text-xs text-gray-500">{liveRoute.object.name}</span>
              )}
            </p>
          </div>
          {canEdit && (
            <GearButton
              label="Change how calls reach this line"
              onClick={() => { save.reset(); setPick(type); setSheet('choose'); }}
            />
          )}
        </div>
        {type === 'ring_group' && !line.ringGroupNum && (
          <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
            No ring group is recorded for this line, so calls have nothing to reach. Change the
            route and set it again — the message from the last attempt is in the audit log.
          </p>
        )}
        {/* THE DESTINATION'S OWN ROWS. Kevin, 2026-08-27: "If the route is ring group, the card
            should show specific configurable items for a ring group. Each row should look like a
            table row with a kabab menu or an action button(s) at the end of the row. indentation
            should be used to show relationships."
            One card, one gear, and the rows depend on what answers. */}
        <table className="mt-4 w-full">
          <tbody>
            {showRingRows && (
              <>
                {/* "Rings — Ring all at once" said the same word twice. */}
                <Row label="How it rings"
                  action={canEdit ? { label: 'Change', onClick: () => { save.reset(); setSheet('ring_group'); } } : null}>
                  {STRATEGIES.find(([v]) => v === (line.ringStrategy || 'ringall'))?.[1] ?? line.ringStrategy}
                </Row>
                <Row label="Rings for">{line.ringTime ?? 20} seconds</Row>
                {/* The members ARE the line's extensions. A second membership list would be two
                    views of one thing, so this points at the card that owns them. */}
                <Row label="Members" muted={(line.ringsCount ?? 1) < 2}>
                  {(line.ringsCount ?? 1) < 2
                    ? 'One extension — the group behaves the same until you add more below'
                    : `The ${line.ringsCount} extensions below`}
                </Row>
              </>
            )}

            {/* A menu's rows, and — on a line with none — the one row that offers to build one. */}
            {showMenuRows && (
              <AttendantRows
                data={liveRoute}
                tree={routeTree}
                editable={canEdit && (liveRoute?.kind !== 'ivr' || liveRoute?.object?.ownership === 'exclusive')}
                onEdit={setRowModal} />
            )}
          </tbody>
        </table>

        {/* THE DISAGREEMENT, once. Verso's record is what it would BUILD; the host is what answers.
            Saying both is the point of this card — showing two sets of rows was not. */}
        {drifted && (
          <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            Recorded as {(SUMMARY[type] || type).toLowerCase()}, but calls are going to{' '}
            {LIVE_WORDS[liveKind] || 'something else'}. The rows show what callers get today —
            applying would change them to match the record.
          </p>
        )}

        {/* SHARED, and only when it is genuinely shared. `platform` used to land here too and read
            "No number in Verso points at this, so it is not a customer's. Both hosts'
            not-in-service announcement is shared this way" — internal reasoning, in front of an
            operator, on a line whose menu was working fine. */}
        {liveRoute?.kind === 'ivr' && liveRoute?.object?.ownership === 'shared' && (
          <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            More than one number uses this menu, so its greeting is not one customer&rsquo;s to change.
          </p>
        )}

        {type === 'ring_group' && (
          <>
            {/* WHAT THE HOST HAS. Verso reads and writes these — agentClient carries all four
                calls and every save pushes — so the card reports the group's sync state
                instead of confessing an inability it never had (2026-08-11). */}
            <p className={`mt-3 text-xs ${line.ringGroupSync === 'synced' ? 'text-gray-500' : 'text-amber-800'}`}>
              {line.ringGroupSync === 'synced'
                ? `Pushed to ${line.pbxName || 'the PBX'}.`
                : line.ringGroupSync === 'error'
                  ? `The last push to ${line.pbxName || 'the PBX'} failed — Apply to try again.`
                  : `Not yet pushed to ${line.pbxName || 'the PBX'} — Apply to send it.`}
            </p>
          </>
        )}
      </div>

      {sheet === 'choose' && chooseSheet}
      {/* The destination's own modals, rendered OUTSIDE the table — a modal inside a `<tbody>` is
          invalid HTML, which is why the rows ask rather than open. */}
      {rowModal === 'greeting' && (
        <GreetingModal line={line} hostRecordings={liveRoute?.object?.host_recordings}
          onClose={() => setRowModal(null)} />
      )}
      {rowModal === 'add' && <AddMenuModal line={line} onClose={() => setRowModal(null)} />}
      {rowModal === 'keys' && (
        <KeysModal line={line} data={liveRoute} onClose={() => setRowModal(null)} />
      )}
    </>
  );
}

function RingGroupFields({ line, strategy, setStrategy, time, setTime }) {
  return (
    <>
      <p className="text-sm text-gray-600">
        The number rings several extensions. Set how, then add them on this tab once it is
        saved.
      </p>
      <div>
        <label htmlFor="rg-strategy" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
          Strategy
        </label>
        <select
          id="rg-strategy"
          value={strategy}
          onChange={(e) => setStrategy(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          {STRATEGIES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="rg-time" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
          Rings for (seconds)
        </label>
        <input
          id="rg-time"
          type="number"
          min={5}
          max={300}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>
      {/* Recorded, not pushed: one button on this page reaches outside and it is not this
          one (Kevin, 2026-08-11). Saving used to push, which broke that contract and changed
          live call routing the instant an edit was typed. */}
      <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        Recorded in Verso. Save and Apply sends it to {line.pbxName || 'the PBX'}.
      </p>
    </>
  );
}
